import React, { useCallback, useMemo, useState } from "react";
import { Popover, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import CustomButton from "components/common/Button";
import { hasPermission } from "hooks/usePermission";
import InlineLoader from "./InlineLoader";
import { listBillingCharges } from "services/billing";
import type { BsPaginated } from "services/billing";

import CustomerFilter from "./CustomerFilter";
import RetryChargeModal from "./RetryChargeModal";
import { useCursorList } from "./useCursorList";
import { CustomChip, TableComponent } from "./ui";
import FilterSelect from "./FilterSelect";
import { BsCharge, CHARGE_STATUS_OPTIONS, formatDate, formatMoney, statusColor, titleCase } from "./types";

// [QA feedback, PRD-3443] A long failure reason is clipped to a short,
// single-line ellipsis; clicking it opens a popover with the full message
// and an explicit close (X) button, instead of scrolling a wide cell.
const FailureReasonCell = ({ value }: { value: string }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  if (!value) return <>—</>;
  return (
    <>
      <span
        onClick={(e) => setAnchorEl(e.currentTarget)}
        style={{
          display: "inline-block",
          maxWidth: 140,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          verticalAlign: "bottom",
          cursor: "pointer",
        }}
      >
        {value}
      </span>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div style={{ padding: 12, paddingTop: 8, width: 280, position: "relative" }}>
          <IconButton
            onClick={() => setAnchorEl(null)}
            size="small"
            aria-label="close"
            style={{ position: "absolute", top: 4, right: 4 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          <div
            style={{
              fontSize: 12,
              lineHeight: 1.5,
              paddingTop: 20,
              paddingRight: 8,
              maxHeight: 160,
              overflowY: "auto",
              wordBreak: "break-word",
              overflowWrap: "anywhere",
            }}
          >
            {value}
          </div>
        </div>
      </Popover>
    </>
  );
};

// [PRD-3791] Shared with the customer details pop-up's Charges tab, which leaves out the
// Customer column (it's already one customer).
export const buildChargeColumns = (
  canRetry: boolean,
  onRetry: (charge: BsCharge) => void,
  maxAttemptByInvoice: Record<string, number>,
  { showCustomer = true }: { showCustomer?: boolean } = {}
) => [
  ...(showCustomer ? [{ id: "customerName", label: "Customer", render: (v: string) => v || "—" }] : []),
  {
    id: "status",
    label: "Status",
    render: (v: string) => <CustomChip label={titleCase(v)} size="small" color={statusColor(v)} />,
  },
  // [PRD-3443] Placed right after Status (not at the far right) so a long
  // Failure reason never pushes it off-screen, requiring a horizontal scroll.
  ...(canRetry
    ? [
        {
          id: "actions",
          label: "",
          render: (_v: unknown, row: BsCharge) => {
            // A charge row is immutable history. Once a later attempt exists
            // for the same invoice, this row is superseded — retrying it
            // would always 409 ("already has a succeeded/pending charge"),
            // so only the invoice's latest attempt ever offers Retry.
            const isLatestAttempt = row.attemptNumber === maxAttemptByInvoice[row.invoiceId];
            return row.status === "failed" && isLatestAttempt ? (
              <CustomButton label="Retry" onClick={() => onRetry(row)} />
            ) : null;
          },
        },
      ]
    : []),
  { id: "amountCents", label: "Amount", render: (v: number, row: BsCharge) => formatMoney(v, row.currency) },
  // [PRD-3765] One row per invoice (its latest attempt) — show how many attempts it took.
  {
    id: "attemptCount",
    label: "Attempts",
    render: (v: number | undefined, row: BsCharge) => v ?? row.attemptNumber,
  },
  { id: "invoiceDueDate", label: "Invoice date", render: (v: string | null | undefined) => formatDate(v) },
  // [PRD-3765] Real Stripe charge time; migrated history has charged_at, charges made
  // in billing-service fall back to created_at (same moment as the Stripe charge).
  {
    id: "chargedAt",
    label: "Charged on",
    render: (v: string | null | undefined, row: BsCharge) => formatDate(v || row.createdAt),
  },
  { id: "failureReason", label: "Failure", render: (v: string) => <FailureReasonCell value={v} /> },
];


export default function ChargesTab() {
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState("");
  const [selectedCharge, setSelectedCharge] = useState<BsCharge | null>(null);
  // [PRD-3765] latestOnly: one row per invoice (its latest attempt); the status filter
  // applies to that latest attempt.
  const load = useCallback(
    (cursor?: string) =>
      listBillingCharges({
        customerId,
        status: status || undefined,
        latestOnly: true,
        cursor,
        limit: 25,
      }) as Promise<BsPaginated<BsCharge>>,
    [customerId, status]
  );
  const { rows, hasMore, loading, loadingMore, error, loadMore, reload } = useCursorList<BsCharge>(load);

  // [PRD-3443] Manual retry (billing.manage) — reuses billing-service's existing
  // dunning-collection path. No condition on the customer's migration status:
  // this tab already reads billing-service's own charge data, so every row
  // here IS a billing-service charge.
  const canRetry = hasPermission("billing.manage");
  const maxAttemptByInvoice = useMemo(() => {
    const max: Record<string, number> = {};
    for (const row of rows) {
      max[row.invoiceId] = Math.max(max[row.invoiceId] ?? 0, row.attemptNumber);
    }
    return max;
  }, [rows]);
  const columns = buildChargeColumns(canRetry, setSelectedCharge, maxAttemptByInvoice);

  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1 items-end" style={{ flexWrap: "wrap" }}>
        <CustomerFilter onChange={setCustomerId} />
        <FilterSelect
          name="charges-status"
          label="Status"
          options={CHARGE_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
        />
      </div>
      {loading ? (
        <InlineLoader />
      ) : error ? (
        <p className="para-body-m-regular color-text-body-secondary">{error}</p>
      ) : (
        <>
          <TableComponent columns={columns} data={rows} disableSort />
          {hasMore && (
            <div className="flex justify-center mt-05">
              <CustomButton label={loadingMore ? "Loading…" : "Load more"} onClick={loadMore} isDisabled={loadingMore} />
            </div>
          )}
        </>
      )}
      {selectedCharge && (
        <RetryChargeModal
          charge={selectedCharge}
          onClose={() => setSelectedCharge(null)}
          onDone={reload}
        />
      )}
    </div>
  );
}
